import qs from 'qs';
import { useCallback } from 'react';
import { type RouteComponentProps } from 'react-router-dom';

export interface PaginationUrlOptions {
  init?: object | null;
}

/**
 * Fornisce l'oggetto query indicato nella querystring dell'URL.
 * Insieme a `setPaginationUrlQuery` permette la gestione delle query da URL
 * anziché come stato del componente, garantendo il mantenimento dei filtri
 * attraverso la navigazione della cronologia.
 * @param options opzioni aggiuntive (se presenti nella querystring, verranno sovrascritte)
 */
export function getPaginationUrlQuery<T>(
  querystring: string,
  options?: any,
): T {
  return {
    ...(options ?? {}),
    ...qs.parse(querystring, { ignoreQueryPrefix: true }),
  };
}

export type RouteHistory<T> = RouteComponentProps<any, any, T>['history'];

/**
 * Imposta la querystring dell'URL con le informazioni passate come query.
 * Insieme a `getPaginationUrlQuery` permette la gestione delle query da URL
 * anziché come stato del componente, garantendo il mantenimento dei filtri
 * attraverso la navigazione della cronologia.
 * @param input query che verrà inserita nell'URL
 */
export function setPaginationUrlQuery<T>(history: RouteHistory<T>, input: T) {
  history.push(
    history.location.pathname +
      qs.stringify(input, {
        addQueryPrefix: true,
        skipNulls: true,
      }),
  );
}

interface IUseSetPaginationUrlQueryOptions<T> {
  onBeforeSetQuery?: (input: T) => any;
}

/**
 * Hook che ritorna la funzione `setPaginationUrlQuery` con il parametro `history` già impostato.
 */
export function useSetPaginationUrlQuery<T>(
  history: RouteHistory<any>,
  options: IUseSetPaginationUrlQueryOptions<T>,
) {
  const { onBeforeSetQuery } = options;
  const setQuery = useCallback(
    (input: T) => {
      onBeforeSetQuery?.(input);
      setPaginationUrlQuery<T>(history, input);
    },
    [history, onBeforeSetQuery],
  );
  return setQuery;
}

export function usePaginationUrlQueryState<T>(props: {
  history: RouteHistory<any>;
  input: T;
  /**
   * Se fornito, permette di persistere la query in localStorage.
   */
  persistKey?: string;
}): IQueryPaginationHooks<T> {
  const { history, persistKey: rawPersistKey } = props;
  const persistKey = rawPersistKey ? `qry-${rawPersistKey}` : undefined;

  /**
   * Prima di impostare la query, nell'URL, la salva in localStorage.
   */
  const onBeforeSetQuery = useCallback(
    (input: T) => {
      if (persistKey) {
        localStorage.setItem(persistKey, JSON.stringify(input));
      }
    },
    [persistKey],
  );

  /**
   * Se è abilitato il persist, e non c'è già una query nell'URL, la recupera
   * da localStorage ed aggiorna la query nell'URL.
   */
  if (persistKey) {
    const savedSearch = localStorage.getItem(persistKey);
    if (savedSearch && history.location.search === '') {
      const loadedSearch = qs.stringify(JSON.parse(savedSearch));
      history.replace({
        ...history.location,
        search: `?` + loadedSearch,
      });
    }
  }

  const query = getPaginationUrlQuery<T>(history.location.search, props.input);
  const setQuery = useSetPaginationUrlQuery<T>(history, {
    onBeforeSetQuery,
  });

  return [query, setQuery];
}

export type IUseSetPaginationUrlQuery<T> = (input: T) => void;

export type IQueryPaginationHooks<T> = [T, IUseSetPaginationUrlQuery<T>];
